// src/pages/MyProfile.tsx
import React, { useState } from 'react';
import {
  UserCircle,
  Mail,
  Building,
  ShieldCheck,
  KeyRound,
  CheckCircle2,
  Lock,
  BadgeAlert,
  Calendar,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';

export const MyProfile: React.FC = () => {
  const { user } = useAuth();
  const { success, error: toastError } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword) {
      toastError('Current password is required.');
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      toastError('New password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toastError('New passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await apiRequest('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      success('Password successfully changed.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      toastError(err.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900">My Profile & Security</h2>
        <p className="text-xs text-slate-500">
          Manage your enterprise profile settings and authentication credentials
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* User Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col items-center text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-tr from-indigo-600 to-indigo-800 text-white font-bold text-2xl shadow-lg mb-4">
            {user?.fullName ? user.fullName.substring(0, 2).toUpperCase() : 'U'}
          </div>

          <h3 className="text-base font-bold text-slate-900">{user?.fullName}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{user?.email}</p>

          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
              <ShieldCheck className="w-3.5 h-3.5" />
              {user?.role?.name}
            </span>
            <Badge status={user?.status || 'active'} size="sm" />
          </div>

          <div className="w-full mt-6 pt-6 border-t border-slate-100 text-left space-y-3 text-xs">
            <div className="flex justify-between items-center text-slate-600">
              <span className="text-slate-400">Department</span>
              <span className="font-semibold text-slate-800">{user?.department?.name || 'Central'}</span>
            </div>
            <div className="flex justify-between items-center text-slate-600">
              <span className="text-slate-400">Account Type</span>
              <span className="font-semibold text-slate-800">
                {user?.role?.isSystemRole ? 'System Administrator' : 'Standard Enterprise User'}
              </span>
            </div>
            <div className="flex justify-between items-center text-slate-600">
              <span className="text-slate-400">Authorized Privileges</span>
              <span className="font-semibold text-slate-800">{user?.permissions?.length || 0} permissions</span>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div id="password" className="md:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
            <KeyRound className="w-5 h-5 text-indigo-600" />
            <div>
              <h3 className="text-sm font-bold text-slate-900">Change Account Password</h3>
              <p className="text-xs text-slate-500">
                Update your security password to protect access to corporate resources
              </p>
            </div>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <Input
              label="Current Password"
              type="password"
              required
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              icon={<Lock className="w-4 h-4" />}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="New Password"
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                icon={<Lock className="w-4 h-4" />}
              />
              <Input
                label="Confirm New Password"
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                icon={<Lock className="w-4 h-4" />}
              />
            </div>

            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-600 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p>
                Passwords are cryptographically salted and hashed on the server using PBKDF2 with 100,000 rounds. Passwords are never stored in plain text or logged.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" variant="primary" loading={loading} icon={<KeyRound className="w-4 h-4" />}>
                Update Password
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Permissions Breakdown */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-1">Assigned Permissions Breakdown</h3>
        <p className="text-xs text-slate-500 mb-4">
          All functional authorizations assigned to your position ({user?.role?.name}):
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {user?.permissions?.map(perm => (
            <div
              key={perm}
              className="flex items-center gap-1.5 p-2 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono text-slate-700"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
              <span className="truncate">{perm}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
